import React, { useCallback, useMemo, useState } from "react";
import { useRecoilValue } from "recoil";
import { useTranslation } from "react-i18next";

import { mergeOrderSelectIdsStore } from "@/store/order";
import { splitOrder as commitSplitOrdr } from "@/api/order";
import { Button, Checkbox, Divider, Form, message } from "antd";
import ChildOrder from "@/components/Order/ChildOrder/";
import { SvgIcon } from "@/components/SvgIcon";
import { math } from "@/utils/math";
import SplitOrder from "@/components/Order/SplitOrder";
import { OrderStore } from "@/store/order";
import dayjs from "dayjs";
import sentry4Action from "@/utils/sentry";
import ClampLines from "react-clamp-lines";
import { IS_MOBILE, toLogin } from "@/utils/tool";
import { getUser } from "@/api/config";
import { SOURCE_ORDER_TYPE } from "@/utils/contants";
// import ClampLines from "@/components/TextClamp";
type Props = {
  order: Order;
  className?: string;
  //是否可以选择
  isCheck?: boolean;

  // 选中发生变化时触发
  updateCheck?: (checked) => void;
  // 拆分订单
  splitOrderSuccess?: (order: Order) => void;
};

const OrderInfo = (props: Props) => {
  const { order, className, isCheck, updateCheck, splitOrderSuccess } = props;
  const { t } = useTranslation();
  const [form] = Form.useForm();
  // 是否已拆分
  const [splited, setSplited] = useState(false);
  const [canSplit, setCanSplit] = useState(false);

  const selectOrderIds = useRecoilValue(mergeOrderSelectIdsStore);
  const rawOrderInfo = useRecoilValue(OrderStore);
  const isMeeting = order.publishing_house_order_type === 2;
  const isBook = order.publishing_house_order_type === 4;
  const [messageApi, contextHolder] = message.useMessage();
  const user = getUser();

  const orderTypeMap = {
    1: t("论文APC订单"),
    2: t("会议服务订单"),
    3: t("PPV订单"),
    4: t("图书BPC订单"),
    5: t("纸本期刊服务订单"),
    6: t("PTP订单"),
  };

  const onChage = () => {
    const checked = selectOrderIds.includes(order.order_id);
    updateCheck(!checked);
  };

  /**
   * 拆分支付
   */
  const onSplitOrder = () => {
    if (!user) {
      toLogin();
      return;
    }
    sentry4Action("拆分支付", {
      order_id: order.order_id,
      order_type: order.expense_type,
      publishing_house_name: order.publishing_house_name,
      journal_name: order.journal_name,
      article_title: order.article_title,
      cae: order.contact_author_email.split("@").join(" "),
      meeting_name: order?.meeting_name,
    });

    setSplited(true);
  };

  /**
   * 取消拆分
   */
  const cancelSplit = () => {
    sentry4Action("取消-拆分支付", {
      order_id: order.order_id,
      order_type: order.expense_type,
      publishing_house_name: order.publishing_house_name,
      journal_name: order.journal_name,
      article_title: order.article_title,
      cae: order.contact_author_email.split("@").join(" "),
      meeting_name: order?.meeting_name,
    });
    setSplited(false);
    form.resetFields();
  };

  /**
   * 提交拆分
   */
  const commitSplit = async () => {
    sentry4Action("提交拆分订单", {
      order_id: order.order_id,
      order_type: order.expense_type,
      publishing_house_name: order.publishing_house_name,
      journal_name: order.journal_name,
      article_title: order.article_title,
      cae: order.contact_author_email.split("@").join(" "),
      meeting_name: order?.meeting_name,
    });
    try {
      const data = await form.validateFields();
      let total = Number(data.money);
      const orders = data.orders.map((item) => {
        return {
          pay_money: Number(item.pay_money),
          apc_user_real_name: item.apc_user_real_name,
          apc_user_mobile: item.apc_user_mobile,
        };
      });
      for (let i = 0; i < orders.length; i++) {
        const element = orders[i];
        total = math
          .add(math.bignumber(total), math.bignumber(element.pay_money))
          .toNumber();
      }

      if (total !== order.total_money) {
        messageApi.open({ content: t("全部拆分订单金额应等于源订单金额") });
        return;
      }

      const res = await commitSplitOrdr<Order>({
        order_id: order.order_id,
        orders: orders,
        pay_money: Number(data.money),
      });

      if (res.code !== 0) {
        if (res.code === 200002) {
          location.reload();
          return;
        }
        messageApi.open({ content: res.msg });
        return;
      }
      cancelSplit();
      const { configuration } = rawOrderInfo;
      res.data.configuration = configuration;
      splitOrderSuccess(res.data);
    } catch (error) {}
  };

  /**
   * 拆分逻辑
   */
  const splitBtnWrapper = () => {
    if (
      order.configuration.enable_split_order &&
      order.allow_split &&
      order.pay_orders.length === 0 &&
      order.pay_status !== 2
    ) {
      return (
        <div className="split-options-box">
          {splited ? (
            <>
              <span className="cancel-split" onClick={cancelSplit}>
                {t("取消")}
              </span>
              <Button className="commit-split" onClick={commitSplit}>
                {t("提交拆分")}
              </Button>
            </>
          ) : (
            <span className="split" onClick={onSplitOrder}>
              {t("拆分支付")}
            </span>
          )}
        </div>
      );
    }
    // }
  };
  const renderContentByOrderType = useMemo(() => {
    let content = null;
    if (isMeeting) {
      content = (
        <>
          <div className="order-item">
            <span> {t("会议名称")}：</span>
            <ClampLines
              lines={3}
              text={order.meeting_name}
              id={`order.meeting_name-${Math.random()}`}
              moreText={t("展开")}
              lessText={t("收起")}
              key={`${Math.random().toString(32).slice(2)}`}
            />
          </div>
          <div className="order-item">
            <span> {t("会议开始时间")}：</span>
            <span>
              {order.meeting_start_time
                ? dayjs.unix(order.meeting_start_time).format("YYYY-MM-DD")
                : ""}
            </span>
          </div>
          <div className="order-item">
            <span> {t("会议结束时间")}：</span>
            <span>
              {order.meeting_end_time
                ? dayjs.unix(order.meeting_end_time).format("YYYY-MM-DD")
                : ""}
            </span>
          </div>
        </>
      );
    } else if (isBook) {
      content = (
        <>
          <div className="order-item">
            <span> {t("图书标题")}：</span>
            <span>
              <ClampLines
                text={order.book_title}
                id={`order.book_title-${Math.random()}`}
                lines={3}
                moreText={t("展开")}
                lessText={t("收起")}
                key={`${Math.random().toString(32).slice(2)}`}
              />
              {/* {order.book_title} */}
            </span>
          </div>
          <div className="order-item">
            <span> {t("联系人")}：</span>
            <span>{order.book_contact_real_name}</span>
          </div>
          <div className="order-item">
            <span> {t("联系人邮箱")}：</span>
            <span>{order.book_contact_email}</span>
          </div>
          <div className="order-item">
            <span> {t("联系人机构")}：</span>
            <span>{order.book_contact_affiliation}</span>
          </div>
        </>
      );
    } else {
      content = (
        <>
          <div className="order-item">
            <span> {t("期刊")}：</span>
            <ClampLines
              lines={3}
              text={order.journal_name}
              id={`order.journal_name-${Math.random()}`}
              moreText={t("展开")}
              lessText={t("收起")}
              key={`${Math.random().toString(32).slice(2)}`}
            />
          </div>
          {order.configuration.show_manuscript_id && (
            <div className="order-item">
              <span> {t("稿件ID")}：</span>
              <span>{order.manuscript_id}</span>
            </div>
          )}
          <div className="order-item">
            <span> {t("文章标题")}：</span>
            <ClampLines
              lines={3}
              text={order.article_title}
              id={`order.article_title-${Math.random()}`}
              moreText={t("展开")}
              lessText={t("收起")}
              key={`${Math.random().toString(32).slice(2)}`}
            />
          </div>
          {order.configuration.show_ptp_detail && (
            <div className="order-item">
              <span>PTP：</span>
              <div>
                {order.ptp_detail
                  ?.split(";")
                  .map((detail, index) => <div key={index}>{detail}</div>)}
              </div>
            </div>
          )}
          {order.configuration.show_author_email && (
            <div className="order-item">
              <span> {t("作者邮箱")}：</span>
              <span>
                {order.order_type === SOURCE_ORDER_TYPE.Ptp
                  ? order.ptp_contact_email
                  : order.contact_author_email}
              </span>
            </div>
          )}
        </>
      );
    }
    return content;
  }, [isMeeting, isBook, order, t]);
  return (
    <>
      {contextHolder}
      <div
        style={
          !IS_MOBILE
            ? {
                border: "1px solid var(--kx-border-2)",
                borderRadius: 8,
              }
            : {}
        }
      >
        <div
          className={`order-info ${className || ""} ${
            selectOrderIds.includes(order.order_id) ? "selected" : ""
          }`}
          style={{
            backgroundColor: `${IS_MOBILE ? "white" : "inherit"}`,
            paddingTop: `${isCheck ? "1px" : "1.333rem"}`,
          }}
        >
          {isCheck && (
            <div className="order-item code" style={{ marginBottom: "1rem" }}>
              <Checkbox
                checked={selectOrderIds.includes(order.order_id)}
                className="order-checkbox"
                onChange={onChage}
              ></Checkbox>
              <span style={{ color: "inherit" }}>{t("订单号")}</span>
              <span style={IS_MOBILE ? { wordBreak: "break-all" } : {}}>
                {order.publishing_house_order_id}
              </span>
            </div>
          )}
          {order.order_id && (
            <div className="order-item">
              <span> {t("支付号")}：</span>
              <span>{order.order_id}</span>
            </div>
          )}

          {!isMeeting && order.configuration.show_expense_type && (
            <div className="order-item">
              <span> {t("订单类型")}：</span>
              <span>{orderTypeMap[order.order_type]}</span>
            </div>
          )}
          {order.configuration.show_publishing_house && (
            <div className="order-item">
              <span> {t("出版社")}：</span>
              <ClampLines
                text={order.publishing_house_name}
                id={`order.publishing_house_name-${Math.random()}`}
                lines={3}
                moreText={t("展开")}
                lessText={t("收起")}
                key={`${Math.random().toString(32).slice(2)}`}
              />
            </div>
          )}
          {renderContentByOrderType}
          <div className="line">
            <span></span>
          </div>
          <div className="order-money">
            <div className="order-money-left">
              <span> {t("金额")}：</span>
              <span className="symbol">¥</span>
              <span className="money">{order.total_money.toFixed(2)}</span>
            </div>
            {splitBtnWrapper()}
            {order.pay_orders.length > 0 && (
              <div
                className="splited-btn"
                onClick={(e) => setCanSplit(!canSplit)}
              >
                <span> {t("查看拆分列表")}</span>
                <SvgIcon
                  icon={
                    canSplit
                      ? "icon-a-xianxingxiangshang"
                      : "icon-a-xianxingxiangxia"
                  }
                  width={16}
                  height={16}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      {order.pay_orders.length > 0 && canSplit && (
        <ChildOrder childOrdrList={order.pay_orders}></ChildOrder>
      )}
      {order.allow_split && splited && (
        <div className="split-box">
          <SplitOrder
            order={order}
            form={form}
            className="allow-split"
          ></SplitOrder>
        </div>
      )}
    </>
  );
};

export default OrderInfo;
